import { getCategoryColor } from '@app/modules/logger/screens/stream/features/live-topics/shared/topic-category-chip/category-colors';
import { Chip, useTheme, type ChipProps } from '@mui/material';

export interface TopicCategoryChipProps extends Omit<ChipProps, 'label' | 'variant' | 'size'> {
  /** Category label (e.g. "Política", "Economía"). */
  category: string;
  /** Chip size. Defaults to "small". */
  size?: 'small' | 'medium';
  /** "filled" uses category color as background (e.g. for active list item); "outlined" only border and text. */
  variant?: 'outlined' | 'filled';
}

/**
 * Shared chip for displaying a live topic category with consistent color and typography.
 * Uses getCategoryColor for border/background and caption typography by default.
 * Forwards remaining Chip props (e.g. onDelete from Autocomplete getTagProps).
 */
export function TopicCategoryChip({
  category,
  size = 'small',
  variant = 'outlined',
  sx,
  ...chipProps
}: TopicCategoryChipProps) {
  const { palette } = useTheme();
  const color = getCategoryColor(category, palette.mode);
  return (
    <Chip
      label={category}
      variant={variant}
      size={size}
      sx={{
        ...(variant === 'filled'
          ? { backgroundColor: color, color: 'common.white' }
          : { borderColor: color, color }),
        '& .MuiChip-label': { typography: 'caption' as const },
        ...sx,
      }}
      {...chipProps}
    />
  );
}
